import { PRODUCT_DEFAULT_VAT_PERCENTAGE } from "~config/shop";
import { formatCurrency } from "~utils/format";

/**
 * @typedef {object} Addon
 * @prop {string} id
 * @prop {number} quantity
 * @prop {number} price
 */

/**
 * @param {"daily"|"monthly"|"yearly"} productFrequency
 *
 * @returns {number}
 */
function getPriceMultiplier(productFrequency) {
  switch (productFrequency) {
    case "daily":
      return 1 / 31; // for debugging purposes only, so we just divide the price by 31
    case "monthly":
      return 1;
    case "yearly":
      return 12;
    default:
      throw new Error("Invalid product frequency");
  }
}

/**
 *
 * @param {object} values
 * @param {Addon} values.addon
 * @param {"daily"|"monthly"|"yearly"} values.frequency
 * @returns {number}
 */
export function getAddonPrice({ addon, frequency }) {
  if (addon.price === 0) {
    return 0;
  }
  const multiplier = getPriceMultiplier(frequency);
  return addon.price * multiplier * (addon.quantity ?? 1);
}

/**
 *
 * @param {object} values
 * @param {"daily"|"monthly"|"yearly"} values.frequency
 * @param {Array<Addon>} values.addons
 * @returns {number}
 */
export function getCombinedAddonPrices({ frequency, addons = [] }) {
  if (addons.length === 0) {
    return 0;
  }
  return addons.reduce(
    (acc, addon) => acc + getAddonPrice({ addon, frequency }),
    0,
  );
}

/**
 *
 * @param {object} values
 * @param {"daily"|"monthly"|"yearly"} values.frequency
 * @param {number} values.price
 * @param {number} values.quantity
 * @returns {number}
 */
export function getSingleProductPrice({ price, quantity, frequency }) {
  if (price === 0) {
    return 0;
  }
  const multiplier = getPriceMultiplier(frequency);
  const adjustedPrice = price * multiplier;
  return adjustedPrice * quantity;
}

/**
 *
 * @param {object} values
 * @param {"daily"|"monthly"|"yearly"} values.frequency
 * @param {number} values.price
 * @param {number} values.quantity
 * @param {number} [values.vat=9]
 * @param {Array<Addon>} [values.addons=[]]
 * @returns {{
 *  totalPrice: number,
 *  totalVat: number,
 *  subtotal: number
 * }}
 */
export function getCombinedSingleProductPrices({
  price,
  quantity,
  frequency,
  vat = PRODUCT_DEFAULT_VAT_PERCENTAGE,
  addons = [],
}) {
  if (price === 0) {
    return {
      totalPrice: 0,
      totalVat: 0,
      subtotal: 0,
    };
  }
  const totalPrice =
    getSingleProductPrice({ price, quantity, frequency }) +
    getCombinedAddonPrices({ frequency, addons });
  const vatPercentage = isNaN(Number(vat))
    ? 100 + PRODUCT_DEFAULT_VAT_PERCENTAGE
    : 100 + Number(vat);
  const totalVat = (totalPrice / vatPercentage) * vat;
  const subtotal = totalPrice - totalVat;
  return {
    totalPrice,
    totalVat,
    subtotal,
  };
}

/**
 *
 * @param {import("next-i18next").TFunction} t
 * @param {number} price
 * @param {"daily"|"monthly"|"yearly"} frequency
 * @returns
 */
export function formatSubscriptionPrice(t, price, frequency) {
  switch (frequency) {
    case "yearly":
      return `${formatCurrency(price)} ${t("perYear")}`;
    case "daily":
      return `${formatCurrency(price)} ${t("perDay")}`;
    case "monthly":
    default:
      return `${formatCurrency(price)} ${t("perMonth")}`;
  }
}
